import React from "react";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import AutocompleteField from "../../field/AutocompleteField";

import FloatField from "../../base/FloatField";
import DateService from "../../../services/dateService";
import { useTranslation } from "react-i18next";

import { useAsync } from "../../../hooks/useAsync";

import useEquipmentService from "../../../services/equipmentService";
import TextFieldFormControl from "../../field/TextFieldFormControl";

import {
  EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE,
  EQUIPMENT_USAGE_METHOD_LIST,
  EQUIPMENT_USAGE_METHOD_READ_ONLY_LIST,
  EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION,
  EQUIPMENT_USAGE_METHOD_MANUAL,
} from "../../../helpers/constants";

import LoaderWrapper from "../../wrapper/LoaderWrapper";
import { convertFlatListFromBackendToTranslatedOptionObject } from "../../../helpers/methods";

const EquipmentUsageForSchemeForm = React.memo((props) => {
  const { t } = useTranslation();
  const { getEquipmentBmsSelectList } = useEquipmentService();

  const bmsSelectList = useAsync(() => {
    if (
      !props.formValue ||
      !props.formValue?.equipment ||
      !props.bmsEnd ||
      !props.bmsStart
    )
      return Promise.resolve(null);
    return getEquipmentBmsSelectList({
      equipment: props.formValue?.equipment,
      equipbms_date_start: props.bmsStart,
      equipbms_date_end: props.bmsEnd,
    });
  }, [props.formValue?.equipment, props.bmsEnd, props.bmsStart]);

  const isMethodAverageCompsumption =
    props.formValue?.estseu_usage_method ===
    EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION;

  const isMethodReadingDifference =
    props.formValue?.estseu_usage_method ===
    EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE;

  const calculateUsageUsingAverageCompsumptionMethod = (bmsStart, bmsEnd) => {
    const bmsStartDate = new Date(bmsStart?.equipbms_date).setHours(0, 0, 0, 0);
    const bmsEndDate = new Date(bmsEnd?.equipbms_date).setHours(0, 0, 0, 0);

    const dateDiff =
      DateService.getDaysBetweenDates(bmsStartDate, bmsEndDate) + 1;

    const valueDiff = bmsEnd?.equipbms_value - bmsStart?.equipbms_value;

    const { zeroHour: startMonth, endHour: endMonth } =
      DateService.getISOStringZeroAndEndHoursOfMonthAndYear(
        new Date(
          props.formValue.estseu_usage_year,
          props.formValue.estseu_usage_month,
          0,
        ),
      );

    const startSettlement = DateService.getBiggerDate(
      props.formValue.eits_start_settlement
        ? new Date(props.formValue.eits_start_settlement)
        : new Date(startMonth),
      new Date(startMonth),
    );

    const endSettlement = DateService.getSmallerDate(
      props.formValue.eits_end_settlement
        ? new Date(props.formValue.eits_end_settlement)
        : new Date(endMonth),
      new Date(endMonth),
    );

    let dayInSettlement = DateService.getDaysBetweenDates(
      startSettlement,
      endSettlement,
    );

    if (
      props.formValue.eits_start_settlement > startMonth &&
      props.formValue.eits_start_settlement < endMonth
    ) {
      dayInSettlement += 1;
    }

    return ((valueDiff / dateDiff) * dayInSettlement).toFixed(2);
  };

  const calculateUsageUsingReadingDifferenceMethod = (bmsStart, bmsEnd) => {
    return (bmsEnd?.equipbms_value - bmsStart?.equipbms_value).toFixed(2);
  };

  const handleChangeEquipmentBms = (...inputProps) => {
    const { name, value } = props.onChangeAutocompleteFieldWithObjectOptions(
      props.index,
      ...inputProps,
    );

    const bmsStart = bmsSelectList.value.find(
      (bms) =>
        bms.id ===
        (name === "equipment_bms_start"
          ? value
          : props.formValue.equipment_bms_start),
    );

    const bmsEnd = bmsSelectList.value.find(
      (bms) =>
        bms.id ===
        (name === "equipment_bms_end"
          ? value
          : props.formValue.equipment_bms_end),
    );

    let eitsUsageMethod = EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE;
    let eitsUsageValue = props.formValue.estseu_usage_value;
    if (isMethodAverageCompsumption) {
      eitsUsageValue = calculateUsageUsingAverageCompsumptionMethod(
        bmsStart,
        bmsEnd,
      );
      eitsUsageMethod = EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION;
    }

    if (isMethodReadingDifference) {
      eitsUsageValue = calculateUsageUsingReadingDifferenceMethod(
        bmsStart,
        bmsEnd,
      );
    }

    props.setFormValue((prevFormValue) =>
      prevFormValue.map((element, _index) =>
        _index === props.index
          ? {
              ...element,
              estseu_usage_value: eitsUsageValue,
              estseu_usage_method: eitsUsageMethod,
              [name]: value,
            }
          : element,
      ),
    );
  };

  const handleChangeEitsUsageValue = (e) => {
    props.setFormValue((prevFormValue) =>
      prevFormValue.map((element, _index) =>
        _index === props.index
          ? {
              ...element,
              estseu_usage_value: e.target.value,
              estseu_usage_method: EQUIPMENT_USAGE_METHOD_MANUAL,
            }
          : element,
      ),
    );
  };

  const handleChangeEitsUsageMethod = (...inputProps) => {
    const { name, value } = props.onChangeAutocompleteFieldWithObjectOptions(
      props.index,
      ...inputProps,
    );

    const bmsStart = bmsSelectList.value.find(
      (bms) => bms.id === props.formValue.equipment_bms_start,
    );

    const bmsEnd = bmsSelectList.value.find(
      (bms) => bms.id === props.formValue.equipment_bms_end,
    );

    let eitsUsageValue = props.formValue.estseu_usage_value;
    if (value === EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION) {
      eitsUsageValue = calculateUsageUsingAverageCompsumptionMethod(
        bmsStart,
        bmsEnd,
      );
    }

    if (value === EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE) {
      eitsUsageValue = calculateUsageUsingReadingDifferenceMethod(
        bmsStart,
        bmsEnd,
      );
    }

    props.setFormValue((prevFormValue) =>
      prevFormValue.map((element, _index) =>
        _index === props.index
          ? {
              ...element,
              estseu_usage_value: eitsUsageValue,
              [name]: value,
            }
          : element,
      ),
    );
  };

  const isLoading =
    props.formValue === undefined ||
    bmsSelectList.loading ||
    !bmsSelectList.value;

  if (isLoading) return <LoaderWrapper showLoader={true} />;

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      rowGap={1}
      columnSpacing={1}
      mt={2}
      mb={2}
    >
      <Grid item xs={12} sm={1.5}>
        <TextFieldFormControl
          value={props.formValue.equipment_nr}
          label={t("dialog.equipment.equipment_nr")}
          readOnly={true}
        />
      </Grid>
      <Grid item xs={12} sm={3.5}>
        <AutocompleteField
          name="equipment_bms_start"
          isObjectOption={true}
          value={props.formValue.equipment_bms_start}
          options={bmsSelectList.value}
          optionLabelKey={"equipbms_date_value"}
          label={t("dialog.eits_usage.equipment_bms_start")}
          onChange={handleChangeEquipmentBms}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                backgroundColor: option.bms_is_ed_accepted
                  ? "var(--matcha-green)"
                  : option.bms_is_equipment_usage_verified
                    ? "var(--blue-gray)"
                    : null,
              }}
            >
              {option["equipbms_date_value"]}
            </li>
          )}
          multiple={false}
          disableClearable
        />
      </Grid>
      <Grid item xs={12} sm={3.5}>
        <AutocompleteField
          name="equipment_bms_end"
          isObjectOption={true}
          value={props.formValue.equipment_bms_end}
          options={bmsSelectList.value}
          optionLabelKey={"equipbms_date_value"}
          label={t("dialog.eits_usage.equipment_bms_end")}
          onChange={handleChangeEquipmentBms}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                backgroundColor: option.bms_is_ed_accepted
                  ? "var(--matcha-green)"
                  : option.bms_is_equipment_usage_verified
                    ? "var(--blue-gray)"
                    : null,
              }}
            >
              {option["equipbms_date_value"]}
            </li>
          )}
          multiple={false}
          disableClearable
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <AutocompleteField
          name="estseu_usage_method"
          value={props.formValue.estseu_usage_method}
          label={t("dialog.eits_usage.eits_usage_method")}
          options={convertFlatListFromBackendToTranslatedOptionObject(
            EQUIPMENT_USAGE_METHOD_LIST,
          )}
          onChange={handleChangeEitsUsageMethod}
          isObjectOption={true}
          disableClearable
          getOptionDisabled={(option) =>
            EQUIPMENT_USAGE_METHOD_READ_ONLY_LIST.includes(option.id)
          }
        />
      </Grid>
      <Grid item xs={12} sm={1.5}>
        <FloatField
          name={"estseu_value"}
          label={t("dialog.eits_usage.eits_usage_value")}
          value={props.formValue.estseu_usage_value}
          onChange={handleChangeEitsUsageValue}
          suffix={props.formValue.estseu_usage_unit}
        />
      </Grid>
    </Grid>
  );
});

EquipmentUsageForSchemeForm.propTypes = {
  index: PropTypes.number,
  formValue: PropTypes.object,
  setFormValue: PropTypes.func,
  onChange: PropTypes.func,
  onChangeAutocompleteFieldWithObjectOptions: PropTypes.func,
  onChangeDate: PropTypes.func,
};

EquipmentUsageForSchemeForm.defaultProps = {};

export default EquipmentUsageForSchemeForm;
